import { useEffect, useRef, useState } from 'react'

import {
  EMPTY_FUNCTION,
  EMPTY_LIST,
  EMPTY_OBJECT,
} from '../../../../constants/common'
import { GretaAIChatStatus } from '../../GretaAIChat.constants'
import { getIsGretaAIChatThinking } from '../../GretaAIChat.utils'
import { useGretaAIChatChatCreation } from '../useGretaAIChatChatCreation'
import { useGretaAIChatGenerationPoll } from '../useGretaAIChatGenerationPoll'
import { useGretaAIChatGenerationWait } from '../useGretaAIChatGenerationWait'
import { useGretaAIChatHistory } from '../useGretaAIChatHistory'
import { useGretaAIChatReplyRequest } from '../useGretaAIChatReplyRequest'
import { useGretaAIChatReplySettle } from '../useGretaAIChatReplySettle'
import { useGretaAIChatTimedOutTurns } from '../useGretaAIChatTimedOutTurns'
import { useGretaAIChatTurnStartedAt } from '../useGretaAIChatTurnStartedAt'

import { useGretaAIChatConversationTurns } from './useGretaAIChatConversationTurns'
import { useGretaAIChatOnlineRecovery } from './useGretaAIChatOnlineRecovery'

export function useGretaAIChatConversation({
  activeChatId = null,
  getIsGenerationSettledRecently = EMPTY_FUNCTION,
  onChatCreated = EMPTY_FUNCTION,
  onChatMissing = EMPTY_FUNCTION,
  onGenerationSettled = EMPTY_FUNCTION,
  onSourceRevoked = EMPTY_FUNCTION,
  onTurnSettled = EMPTY_FUNCTION,
  onTurnStart = EMPTY_FUNCTION,
} = EMPTY_OBJECT) {
  const [messages, setMessages] = useState(EMPTY_LIST)
  const [status, setStatus] = useState(GretaAIChatStatus.Idle)

  const loadedChatIdRef = useRef(null)
  const turnControllersRef = useRef(new Map())

  // Lets async code check the latest chat instead of a stale closure.
  const activeChatIdRef = useRef(activeChatId)

  useEffect(() => {
    activeChatIdRef.current = activeChatId
  }, [activeChatId])

  function getIsOpenConversation(conversationId) {
    return conversationId === activeChatIdRef.current
  }

  function getIsNewChatViewActive() {
    return activeChatIdRef.current === null
  }

  const { clearTurn, preserveOrStartTurn, startTurn, turnStartedAt } =
    useGretaAIChatTurnStartedAt({
      activeChatId,
      isThinking: getIsGretaAIChatThinking(status),
    })

  const {
    checkGenerationSettled,
    getIsPolling,
    pollUntilSettled,
    stopPolling,
  } = useGretaAIChatGenerationPoll()

  const {
    clearTimedOutTurn,
    getIsTimeoutStopPending,
    markTurnTimedOut,
    settleTimeoutStop,
    syncTimedOutTurn,
  } = useGretaAIChatTimedOutTurns()

  const {
    endReplySettle,
    getIsReplySettling,
    isReplySettling,
    startReplySettle,
  } = useGretaAIChatReplySettle({ activeChatId, getIsOpenConversation })

  function getIsTurnInFlight(conversationId) {
    return (
      turnControllersRef.current.has(conversationId) ||
      getIsPolling(conversationId)
    )
  }

  function getIsTurnAwaitingReply(conversationId) {
    return (
      getIsTurnInFlight(conversationId) && !getIsReplySettling(conversationId)
    )
  }

  const {
    abortReloadMessages,
    fetchMessages,
    forgetDeliveredTurn,
    loadOlderMessages,
    reloadMessages,
    syncDeliveredTurn,
    trackDeliveredTurn,
  } = useGretaAIChatHistory({
    activeChatId,
    getIsTimeoutStopPending,
    getIsTurnInFlight: getIsTurnAwaitingReply,
    loadedChatIdRef,
    setMessages,
    setStatus,
    syncTimedOutTurn,
    onChatMissing,
  })

  const {
    cancelQueuedTurn,
    cancelServerTurn,
    clearPendingAnswerLabel,
    enterGenerationWait,
    getIsStopPending,
    pendingAnswerFileName,
    settleGenerationWait,
    trackBackgroundGeneration,
    waitForPendingStop,
  } = useGretaAIChatGenerationWait({
    abortReloadMessages,
    activeChatId,
    clearTurn,
    getIsGenerationSettledRecently,
    getIsOpenConversation,
    getIsTimeoutStopPending,
    getIsTurnInFlight,
    pollUntilSettled,
    preserveOrStartTurn,
    reloadMessages,
    setStatus,
    settleTimeoutStop,
    turnControllersRef,
    onChatMissing,
    onGenerationSettled,
    onTurnSettled,
  })

  useGretaAIChatOnlineRecovery({
    messages,
    reloadMessages,
    setStatus,
    status,
  })

  const { requestReply } = useGretaAIChatReplyRequest({
    cancelServerTurn,
    checkGenerationSettled,
    clearTurn,
    endReplySettle,
    enterGenerationWait,
    fetchMessages,
    forgetDeliveredTurn,
    getIsOpenConversation,
    getIsStopPending,
    markTurnTimedOut,
    reloadMessages,
    setMessages,
    setStatus,
    settleGenerationWait,
    startReplySettle,
    startTurn,
    syncDeliveredTurn,
    trackDeliveredTurn,
    turnControllersRef,
    onGenerationSettled,
    onSourceRevoked,
    onTurnSettled,
    onTurnStart,
  })

  const { abortChatCreation, createChatAndSend } = useGretaAIChatChatCreation({
    getIsNewChatViewActive,
    loadedChatIdRef,
    requestReply,
    setMessages,
    setStatus,
    onChatCreated,
    onSourceRevoked,
  })

  const {
    discardUnsavedChat,
    retryFlaggedMessages,
    retryLastReply,
    sendMessage,
    stopGeneration,
  } = useGretaAIChatConversationTurns({
    abortChatCreation,
    activeChatId,
    cancelQueuedTurn,
    cancelServerTurn,
    clearTimedOutTurn,
    createChatAndSend,
    getIsOpenConversation,
    getIsTimeoutStopPending,
    getIsTurnInFlight,
    messages,
    reloadMessages,
    requestReply,
    setMessages,
    setStatus,
    status,
    turnControllersRef,
    waitForPendingStop,
  })

  function forgetChat(conversationId) {
    turnControllersRef.current.get(conversationId)?.abort()
    turnControllersRef.current.delete(conversationId)
    clearTimedOutTurn(conversationId)
    settleTimeoutStop(conversationId)
    clearTurn(conversationId)
    clearPendingAnswerLabel(conversationId)
    stopPolling(conversationId)
  }

  return {
    discardUnsavedChat,
    forgetChat,
    isReplySettling,
    loadOlderMessages,
    messages: retryFlaggedMessages,
    pendingAnswerFileName,
    reloadMessages,
    retryLastReply,
    sendMessage,
    status,
    stopGeneration,
    trackBackgroundGeneration,
    turnStartedAt,
  }
}
